<template>
    <Head title="My Tasks" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Header -->
        <div class="mb-6">
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">My Tasks</h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                All items assigned to you across different modules
            </p>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav class="-mb-px flex space-x-8" aria-label="Tabs">
                <!-- All Tab -->
                <Link
                    :href="route('my-tasks.index', { module: 'all' })"
                    :class="[
                        currentModule === 'all'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center gap-2'
                    ]"
                >
                    All
                    <span
                        :class="[
                            currentModule === 'all'
                                ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                            'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full'
                        ]"
                    >
                        {{ counts.all }}
                    </span>
                </Link>

                <!-- Helpdesk Tab -->
                <Link
                    :href="route('my-tasks.index', { module: 'helpdesk' })"
                    :class="[
                        currentModule === 'helpdesk'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center gap-2'
                    ]"
                >
                    Helpdesk
                    <span
                        :class="[
                            currentModule === 'helpdesk'
                                ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                            'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full'
                        ]"
                    >
                        {{ counts.helpdesk }}
                    </span>
                </Link>

                <!-- Maintenance Tab -->
                <Link
                    :href="route('my-tasks.index', { module: 'maintenance' })"
                    :class="[
                        currentModule === 'maintenance'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center gap-2'
                    ]"
                >
                    Maintenance
                    <span
                        :class="[
                            currentModule === 'maintenance'
                                ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                            'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full'
                        ]"
                    >
                        {{ counts.maintenance }}
                    </span>
                </Link>

                <!-- Incidents Tab (only if company has incidents module) -->
                <Link
                    v-if="hasModule('incidents')"
                    :href="route('my-tasks.index', { module: 'incidents' })"
                    :class="[
                        currentModule === 'incidents'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center gap-2'
                    ]"
                >
                    Incidents
                    <span
                        :class="[
                            currentModule === 'incidents'
                                ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                            'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full'
                        ]"
                    >
                        {{ counts.incidents }}
                    </span>
                </Link>

                <!-- Inspection & Checklist Tab (only if company has checklists module) -->
                <Link
                    v-if="hasModule('checklists')"
                    :href="route('my-tasks.index', { module: 'checklists' })"
                    :class="[
                        currentModule === 'checklists'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center gap-2'
                    ]"
                >
                    Inspection & Checklist
                    <span
                        :class="[
                            currentModule === 'checklists'
                                ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                            'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full'
                        ]"
                    >
                        {{ counts.checklists }}
                    </span>
                </Link>
            </nav>
        </div>

        <!-- Tasks List -->
        <div class="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <ul v-if="tasks.length > 0" role="list" class="divide-y divide-gray-200 dark:divide-gray-700">
                <li v-for="task in tasks" :key="task.id">
                    <Link :href="task.url" class="block hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <div class="px-4 py-4 sm:px-6">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center min-w-0 flex-1">
                                    <!-- Module Icon -->
                                    <div class="flex-shrink-0">
                                        <component
                                            :is="getModuleIcon(task.module)"
                                            class="h-6 w-6"
                                            :class="getModuleColor(task.module)"
                                        />
                                    </div>

                                    <!-- Task Info -->
                                    <div class="min-w-0 flex-1 px-4">
                                        <div class="flex items-center justify-between">
                                            <p class="text-sm font-medium text-primary-600 dark:text-primary-400 truncate">
                                                {{ task.number }}
                                            </p>
                                            <div class="ml-2 flex-shrink-0 flex">
                                                <span :class="[
                                                    'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                                                    getStatusBadgeClass(task.status)
                                                ]">
                                                    {{ task.status_label }}
                                                </span>
                                            </div>
                                        </div>
                                        <p class="mt-1 text-sm text-gray-900 dark:text-white font-medium">
                                            {{ task.title }}
                                        </p>
                                        <div class="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <span class="flex items-center">
                                                <svg class="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                                {{ task.type }}
                                            </span>
                                            <span v-if="task.priority" class="ml-4 flex items-center">
                                                <svg class="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                </svg>
                                                {{ task.priority }}
                                            </span>
                                            <span v-if="task.due_date" class="ml-4 flex items-center">
                                                <svg class="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                Due: {{ task.due_date }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Link>
                </li>
            </ul>

            <!-- Empty State -->
            <div v-else class="text-center py-12">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tasks found</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    You don't have any pending tasks{{ currentModule !== 'all' ? ' in this module' : '' }}.
                </p>
            </div>
        </div>

        <!-- Task Count -->
        <div class="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
            Showing {{ tasks.length }} task{{ tasks.length !== 1 ? 's' : '' }}
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import {
    TicketIcon,
    WrenchScrewdriverIcon,
    ExclamationTriangleIcon,
    ClipboardDocumentCheckIcon,
} from '@heroicons/vue/24/outline';
import route from '@/Utils/route';

const props = defineProps({
    tasks: {
        type: Array,
        required: true,
    },
    currentModule: {
        type: String,
        required: true,
    },
    counts: {
        type: Object,
        required: true,
    },
});

const page = usePage();

// Check if company has a specific module
const hasModule = (module) => {
    const activeModules = page.props.activeModules || [];
    return activeModules.includes(module);
};

// Get module icon component
const getModuleIcon = (module) => {
    const icons = {
        tickets: TicketIcon,
        work_orders: WrenchScrewdriverIcon,
        incidents: ExclamationTriangleIcon,
        checklists: ClipboardDocumentCheckIcon,
    };
    return icons[module] || TicketIcon;
};

// Get module color class
const getModuleColor = (module) => {
    const colors = {
        tickets: 'text-blue-500',
        work_orders: 'text-green-500',
        incidents: 'text-red-500',
        checklists: 'text-purple-500',
    };
    return colors[module] || 'text-gray-500';
};

// Get status badge color class
const getStatusBadgeClass = (status) => {
    const statusClasses = {
        // Tickets
        open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
        pending: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
        on_hold: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
        resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',

        // Work Orders
        scheduled: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',

        // Incidents
        triage_completed: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
        under_investigation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
        pending_corrective_action: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
        correction_in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',

        // Checklists
        assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        pending_review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
        pending_approval: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    };

    return statusClasses[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
};
</script>
